/**
 * SmartVideo — v2.5.917
 *
 * Drop-in video renderer that abstracts the source. Give it any URL and it
 * figures out whether to render:
 *   • An iframe for Google Drive / YouTube / Vimeo (with autoplay + minimal UI)
 *   • A native <video> for direct MP4/webm/mov files
 *
 * Usage:
 *   <SmartVideo url={mediaAsset.external_url || mediaAsset.file_url}
 *               title="Onboarding video"
 *               autoPlay muted loop />
 */
import { classifyVideoUrl } from '../utils/videoEmbed';

export default function SmartVideo({
  url,
  title = 'video',
  autoPlay = true,
  muted = true,
  loop = false,
  controls = true,
  className = 'w-full h-full',
  poster = '',
}) {
  const info = classifyVideoUrl(url);

  if (info.kind === 'direct') {
    // Same-origin uploaded file or a raw MP4/webm URL — use the native player.
    // Prepend REACT_APP_BACKEND_URL when the URL is a bare /api/uploads/ path.
    const src = info.url.startsWith('/api/uploads/')
      ? `${process.env.REACT_APP_BACKEND_URL}${info.url}`
      : info.url;
    return (
      <video
        src={src}
        poster={poster || undefined}
        title={title}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        controls={controls}
        playsInline
        className={className}
        data-testid="smart-video-direct"
      />
    );
  }

  if (info.kind === 'gdrive' || info.kind === 'youtube' || info.kind === 'vimeo') {
    return (
      <iframe
        src={info.embedUrl}
        title={title}
        allow="autoplay; encrypted-media; fullscreen"
        allowFullScreen
        frameBorder="0"
        className={className}
        data-testid={`smart-video-${info.kind}`}
      />
    );
  }

  // Unknown → show a fallback link so users can click through
  return (
    <div className={`${className} flex items-center justify-center bg-gray-100 text-sm text-gray-500`}>
      <a href={info.url} target="_blank" rel="noopener noreferrer" className="underline">Open video ↗</a>
    </div>
  );
}
